import { useCallback } from "react";
import { useNotify, useRefresh, useUpdate } from "ra-core";
import type { Task } from "@/components/atomic-crm/types";
import { buildTaskRescheduleDueDate } from "@/modules/calendar/calendarEventReschedule";
import { parseDateKey } from "@/modules/calendar/calendarUtils";

const shortDate = (dateKey: string) =>
  parseDateKey(dateKey).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

/**
 * Move or resize a multi-day task (start_date → due_date). Undoable, like
 * calendar drag-and-drop; the due time of day is kept.
 */
export const useTaskRangeReschedule = () => {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  const rescheduleTaskRange = useCallback(
    (task: Task, startDateKey: string, dueDateKey: string) => {
      const currentStart = task.start_date?.slice(0, 10) ?? null;
      const due = buildTaskRescheduleDueDate(task, dueDateKey);
      if (currentStart === startDateKey && due === task.due_date) return;

      update(
        "tasks",
        {
          id: task.id,
          data: { start_date: startDateKey, due_date: due },
          previousData: { ...task },
        },
        { mutationMode: "undoable", onSuccess: () => refresh() },
      );
      notify(
        `Task moved to ${shortDate(startDateKey)} → ${shortDate(dueDateKey)}`,
        { type: "info", undoable: true },
      );
    },
    [notify, refresh, update],
  );

  return { rescheduleTaskRange };
};
